"use client";

import { faPhotoVideo, faTimes } from "@fortawesome/pro-solid-svg-icons";
import {
	Button,
	ScreenOverlay,
	SquareButton,
	cn,
	useMediaQuery,
	useRadixUIPortalFix,
	useSetProperty,
} from "@tosize/ui";
import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";
import { Dialog, VisuallyHidden } from "radix-ui";
import { useMemo } from "react";
import { useMedia } from "~/components/media/Provider";
import { MediaDialogDetail } from "./DialogDetail";
import { MediaDialogOverview } from "./DialogOverview";
import styles from "./media.module.css";

export function MediaDialog() {
	const {
		sortedSections,
		overviewMessage,
		backMessage,
		closeMessage,
		overviewSections,
		detailSection,
		dialogType,
		onMediaChange,
	} = useMedia();

	const router = useRouter();
	const isOpen = useRadixUIPortalFix({ open: !!dialogType });
	const isSmallDevice = useMediaQuery({ max: "sm" });
	const mediaDialogHeader = useSetProperty("--media-dialog-header-y");

	const currentSlider = useMemo(() => {
		if (dialogType === "detail" && detailSection) {
			return sortedSections.find((type) => type.id === detailSection);
		}
	}, [detailSection, dialogType, sortedSections]);

	return (
		<Dialog.Root
			onOpenChange={(open) => onMediaChange({ type: open ? "overview" : undefined })}
			open={isOpen}
		>
			<AnimatePresence>
				{isOpen && (
					<Dialog.Portal forceMount>
						<Dialog.Overlay
							forceMount
							asChild
							onPointerDown={() => onMediaChange({ type: undefined })}
						>
							<ScreenOverlay variant="animated" />
						</Dialog.Overlay>
						<Dialog.Content forceMount asChild onPointerDownOutside={(e) => e.preventDefault()}>
							<motion.div
								className="fixed bottom-0 left-1/2 z-dialog flex h-full w-full flex-col bg-white shadow-lg sm:top-1/2 sm:bottom-auto"
								initial={{
									opacity: 1,
									scale: isSmallDevice ? 1 : 0.9,
									y: isSmallDevice ? "100%" : "-50%",
									x: "-50%",
								}}
								exit={{
									opacity: 0,
									scale: isSmallDevice ? 1 : 0.9,
									y: isSmallDevice ? "100%" : "-50%",
									x: "-50%",
								}}
								animate={{
									opacity: 1,
									scale: 1,
									y: isSmallDevice ? "0%" : "-50%",
									x: "-50%",
								}}
								transition={{ duration: 0.15 }}
							>
								<div
									ref={mediaDialogHeader}
									className="container fixed bottom-0 z-dialog order-last flex w-full items-center justify-between gap-4 bg-white py-4 sm:static sm:bottom-auto sm:order-none"
								>
									<div className="scrollbar-none flex max-w-[calc(100%-56px)] items-center overflow-auto whitespace-nowrap sm:max-w-[calc(100%-64px)]">
										{dialogType === "detail" && (
											<Button
												variant="link"
												className={cn("hidden rounded p-2 sm:inline", styles.anchor)}
												onClick={() => onMediaChange({ type: "overview" })}
											>
												{overviewMessage}
											</Button>
										)}

										{sortedSections.map((section) => (
											<Button
												key={section.id}
												variant="link"
												className={cn("rounded p-2", styles.anchor)}
												onClick={() => {
													return dialogType === "detail"
														? onMediaChange({ type: "detail", id: section.id, index: 0 })
														: router.push(`#${section.id}`);
												}}
												data-active={
													dialogType === "detail"
														? section.id === detailSection
														: overviewSections.includes(section.id)
												}
											>
												{section.title}
											</Button>
										))}

										<VisuallyHidden.Root asChild>
											<Dialog.Title>{currentSlider?.title ?? overviewMessage}</Dialog.Title>
										</VisuallyHidden.Root>
									</div>

									<div className="ml-auto flex items-center justify-center space-x-2">
										{dialogType === "detail" && (
											<SquareButton
												onClick={() => onMediaChange({ type: "overview" })}
												component="button"
												icon={faPhotoVideo}
												className="!text-xl sm:!text-2xl"
												title={backMessage}
												size={isSmallDevice ? "medium" : "large"}
											/>
										)}

										<Dialog.Close asChild>
											<SquareButton
												component="button"
												icon={faTimes}
												title={closeMessage}
												size={isSmallDevice ? "medium" : "large"}
											/>
										</Dialog.Close>
									</div>
								</div>
								<div
									className={cn({
										"group scrollbar-none relative overflow-hidden scroll-smooth":
											dialogType === "detail",
										"scrollbar-none container order-first max-h-[calc(100dvh-var(--media-dialog-header-y,0px))] overflow-auto scroll-smooth pt-4 sm:order-none sm:pt-0":
											dialogType === "overview",
									})}
								>
									{dialogType === "overview" && <MediaDialogOverview />}
									{dialogType === "detail" && <MediaDialogDetail />}
								</div>
							</motion.div>
						</Dialog.Content>
					</Dialog.Portal>
				)}
			</AnimatePresence>
		</Dialog.Root>
	);
}
